/**
 * Utility functions for bulk actions
 */

/**
 * Maximum number of records that can be selected for bulk actions
 */
export const BULK_SELECTION_LIMIT = 1000;

/**
 * Views that display rich cross-entity data and participate in bulk actions.
 * After a bulk action completes, all views in this list (except the current one)
 * are cleared so they re-fetch fresh data on next navigation.
 */
export const BULK_ACTION_VIEWS = [
  "all-patients",
  "visit-schedule",
  "high-priority",
  "top-opportunity",
  "all-applications",
  "pending-renewal",
  "all-claims"
];

/**
 * Entity type configurations.
 * Each action is an object with { actionType, targetEntity? }.
 * targetEntity indicates a cross-entity action (e.g., updating patient fields from enrollment context).
 */
export const ENTITY_CONFIGS = {
  patient: {
    actions: [
      { actionType: "assignees_set" },
      { actionType: "assignees_add" },
      { actionType: "assignees_remove" },
      { actionType: "facilities_set" },
      { actionType: "facilities_add" },
      { actionType: "facilities_remove" },
      { actionType: "physician" },
      { actionType: "status" }
    ]
  },
  enrollment: {
    actions: [
      { actionType: "status" },
      { actionType: "assignee_set" },
      { actionType: "assignees_set", targetEntity: "patient" },
      { actionType: "status", targetEntity: "patient" }
    ]
  },
  claim: {
    actions: [
      { actionType: "status" },
      { actionType: "assignee_set" },
      { actionType: "facility" },
      { actionType: "status", targetEntity: "patient" }
    ]
  }
};

/**
 * Derive a stable string key from an action object.
 * Used for React keys, state comparison, and HTML attributes.
 * @param {Object} action - Action object with { actionType, targetEntity? }
 * @returns {string} Unique key for the action
 */
export const getActionKey = (action) => {
  if (!action) return "";
  return action.targetEntity ? `${action.targetEntity}_${action.actionType}` : action.actionType;
};

/**
 * Action categories describe the UI behavior of each action type:
 * - "status": simple list of status options
 * - "assignee": single-select assignee picker (no mode toggle)
 * - "multi_assignee": multi-select assignee picker with override/add/remove mode toggle
 * - "facility": searchable facility picker
 * - "physician": searchable physician picker with NPI subtext
 */

/**
 * Base action type configurations.
 * Labels are translation keys - use with t() from useTranslation.
 */
const ACTION_CONFIGS = {
  status: { category: "status", labelKey: "bulk_actions.actions.status", icon: "status" },
  facilities_set: { category: "multi_facility", labelKey: "bulk_actions.actions.facility", icon: "facility" },
  facilities_add: { category: "multi_facility", labelKey: "bulk_actions.actions.facility", icon: "facility" },
  facilities_remove: { category: "multi_facility", labelKey: "bulk_actions.actions.facility", icon: "facility" },
  physician: { category: "physician", labelKey: "bulk_actions.actions.physician", icon: "physician" },
  assignee_set: { category: "assignee", labelKey: "bulk_actions.actions.assignee", icon: "assignee" },
  assignees_set: { category: "multi_assignee", labelKey: "bulk_actions.actions.assignee", icon: "assignee" },
  assignees_add: { category: "multi_assignee", labelKey: "bulk_actions.actions.assignee", icon: "assignee" },
  assignees_remove: { category: "multi_assignee", labelKey: "bulk_actions.actions.assignee", icon: "assignee" }
};

/**
 * Get the UI category for an action type.
 * @param {string} actionType - The action type string
 * @returns {string} The category (status, assignee, multi_assignee, facility, physician)
 */
export const getActionCategory = (actionType) => {
  return ACTION_CONFIGS[actionType]?.category || actionType;
};

/**
 * Label overrides when the same actionType needs a different label
 * based on the entity context (e.g., "Status" → "Enrollment Status").
 */
const ENTITY_LABEL_OVERRIDES = {
  enrollment: {
    status: "bulk_actions.actions.enrollment_status",
    assignee_set: "bulk_actions.actions.enrollment_assignee"
  },
  claim: {
    status: "bulk_actions.actions.claim_status",
    assignee_set: "bulk_actions.actions.claim_assignee",
    facility: "bulk_actions.actions.claim_facility"
  }
};

/**
 * Label overrides for cross-entity actions where targetEntity
 * determines the label (e.g., "Patient Status", "Patient Assignee").
 */
const TARGET_ENTITY_LABELS = {
  patient: {
    status: "bulk_actions.actions.patient_status",
    assignees_set: "bulk_actions.actions.patient_assignee"
  }
};

/**
 * Get action display configuration.
 * Use labelKey with t() from useTranslation: t(config.labelKey)
 * @param {Object} action - Action object with { actionType, targetEntity? }
 * @param {string} [entityType] - Entity type for entity-specific labels
 * @returns {Object} Action configuration with labelKey (translation key) and icon
 */
export const getActionConfig = (action, entityType) => {
  const { actionType, targetEntity } = action;
  const config = ACTION_CONFIGS[actionType] || { labelKey: `bulk_actions.actions.${actionType}`, icon: "default" };

  const targetOverride = TARGET_ENTITY_LABELS[targetEntity]?.[actionType];
  if (targetOverride) {
    return { ...config, labelKey: targetOverride };
  }

  const entityOverride = ENTITY_LABEL_OVERRIDES[entityType]?.[actionType];
  if (entityOverride) {
    return { ...config, labelKey: entityOverride };
  }

  return config;
};

/**
 * Format action type for display in CSV header
 * @param {string} actionType - Base action type (status, assignees_set, etc.)
 * @param {string|null} targetEntity - Target entity for cross-entity actions
 * @param {Function} t - Translation function
 * @returns {string} Human-readable action name
 */
const formatActionType = (actionType, targetEntity, t) => {
  const baseMap = {
    status: t("bulk_actions.report.action_update_status"),
    physician: t("bulk_actions.report.action_update_physician"),
    assignee_set: t("bulk_actions.report.action_update_assignee_set"),
    assignees_set: t("bulk_actions.report.action_update_assignee_set"),
    assignees_add: t("bulk_actions.report.action_update_assignee_add"),
    assignees_remove: t("bulk_actions.report.action_update_assignee_remove"),
    facilities_set: t("bulk_actions.report.action_update_facility_set"),
    facilities_add: t("bulk_actions.report.action_update_facility_add"),
    facilities_remove: t("bulk_actions.report.action_update_facility_remove")
  };

  const patientMap = {
    status: t("bulk_actions.report.action_update_patient_status"),
    assignees_set: t("bulk_actions.report.action_update_patient_assignee_set"),
    assignees_add: t("bulk_actions.report.action_update_patient_assignee_add"),
    assignees_remove: t("bulk_actions.report.action_update_patient_assignee_remove")
  };

  if (targetEntity === "patient" && patientMap[actionType]) {
    return patientMap[actionType];
  }
  return baseMap[actionType] || actionType;
};

/**
 * Format entity type for display in CSV header
 * @param {string} entityType - Entity type (patient, enrollment, etc.)
 * @param {Function} t - Translation function
 * @returns {string} Capitalized entity name
 */
const formatEntityType = (entityType, t) => {
  const entityMap = {
    patient: t("bulk_actions.report.entity_patient"),
    enrollment: t("bulk_actions.report.entity_enrollment"),
    claim: t("bulk_actions.report.entity_claim")
  };
  return entityMap[entityType] || entityType.charAt(0).toUpperCase() + entityType.slice(1);
};

/**
 * Format timestamp for display in CSV header
 * @param {Date} timestamp - Date object
 * @returns {string} Formatted date string with timezone
 */
const formatTimestamp = (timestamp) => {
  return timestamp.toLocaleString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZoneName: "short"
  });
};

/**
 * Generate CSV content from bulk action results with metadata header
 * @param {Array} results - Array of result objects with recordId, success, and error
 * @param {Object} metadata - Optional metadata object with actionType, targetEntity, entityType, value, timestamp, summary, t
 * @param {Function} t - Translation function from useTranslation hook
 * @returns {string} CSV content
 */
export const generateCSVReport = (results, metadata = null, t) => {
  const includePatientId = metadata && metadata.entityType !== "patient";

  const headers = [
    t("bulk_actions.report.column_id"),
    ...(includePatientId ? [t("bulk_actions.report.column_patient_id")] : []),
    t("bulk_actions.report.column_status"),
    t("bulk_actions.report.column_error")
  ];

  // Sort results: failures first, then by patient ID, then by record ID
  const sortedResults = [...results].sort((a, b) => {
    if (a.success !== b.success) {
      return a.success ? 1 : -1;
    }
    if (includePatientId && a.patientId !== b.patientId) {
      return (a.patientId || 0) - (b.patientId || 0);
    }
    return a.recordId - b.recordId;
  });

  const rows = sortedResults.map((result) => [
    result.recordId,
    ...(includePatientId ? [result.patientId || ""] : []),
    result.success ? t("bulk_actions.report.status_success") : t("bulk_actions.report.status_failed"),
    result.error || ""
  ]);

  let csvLines = [];

  // Add metadata header if provided
  if (metadata) {
    const { actionType, targetEntity, entityType, value, timestamp, summary } = metadata;

    // Wrap each metadata line in quotes to escape commas
    csvLines.push(`"# ${t("bulk_actions.report.header_title")}"`);
    csvLines.push(`"# ${formatTimestamp(timestamp)}"`);
    csvLines.push(`"# ${t("bulk_actions.report.header_entity")}: ${formatEntityType(entityType, t)}"`);
    const actionLabel = formatActionType(actionType, targetEntity, t);
    csvLines.push(`"# ${t("bulk_actions.report.header_action")}: ${actionLabel}"`);
    csvLines.push(`"# ${t("bulk_actions.report.header_new_value")}: ${value || t("bulk_actions.report.value_na")}"`);
    csvLines.push(`"# ${t("bulk_actions.report.header_total_records")}: ${summary.total}"`);
    csvLines.push(`"# ${t("bulk_actions.report.header_succeeded")}: ${summary.succeeded}"`);
    csvLines.push(`"# ${t("bulk_actions.report.header_failed")}: ${summary.failed}"`);
    csvLines.push(`"#"`);
  }

  // Add data rows
  csvLines.push(headers.join(","));
  // Escape double quotes by doubling them (CSV standard) e.g. "Hello, ""World"" -> "Hello, ""World"""
  csvLines.push(...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")));

  return csvLines.join("\n");
};

/**
 * Format value for display in CSV header
 * Converts IDs to human-readable names when options are available
 * @param {*} value - The value to format (could be ID, array of IDs, or string)
 * @param {string} actionType - Base action type to determine how to format
 * @param {Array} options - Options array with {value, label} objects
 * @param {Function} t - Translation function from useTranslation hook
 * @returns {string} Formatted value for display
 */
export const formatValueForDisplay = (value, actionType, options = [], t) => {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return t("bulk_actions.report.value_na");
  }

  const category = getActionCategory(actionType);

  if (category === "multi_assignee" && Array.isArray(value)) {
    if (value.length === 0) {
      return t("bulk_actions.report.value_none");
    }
    // Map objects with { id, type } to names
    const names = value
      .map((item) => {
        const option = options.find((opt) => opt.value === item.id);
        return option ? option.label : `ID: ${item.id}`;
      })
      .join("; ");
    return names;
  }

  if ((category === "assignee" || category === "facility") && value && typeof value === "object" && value.id) {
    const option = options.find((opt) => opt.value === value.id);
    return option ? option.label : `ID: ${value.id}`;
  }

  // For single value actions (status, facility, physician)
  if (options.length > 0) {
    const option = options.find((opt) => opt.value === value);
    if (option) {
      return option.label;
    }
  }

  // Fallback to string representation
  return String(value);
};

/**
 * Download CSV report
 * @param {Array} results - Array of result objects
 * @param {Object} metadata - Metadata object with actionType, targetEntity, entityType, value, timestamp, summary
 * @param {Function} t - Translation function from useTranslation hook
 */
export const downloadCSVReport = (results, metadata, t) => {
  const csvContent = generateCSVReport(results, metadata, t);
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  const fileName = `bulk_action_report_${metadata.entityType}_${timestamp}.csv`;

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", fileName);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Check if selection limit is reached
 * @param {number} currentCount - Current selection count
 * @returns {boolean} True if limit is reached
 */
export const isSelectionLimitReached = (currentCount) => {
  return currentCount >= BULK_SELECTION_LIMIT;
};

/**
 * Get available actions for an entity
 * @param {string} entity - Entity type
 * @returns {Array<{actionType: string, targetEntity?: string}>} Array of action objects
 */
export const getAvailableActions = (entity) => {
  const config = ENTITY_CONFIGS[entity];
  return config ? config.actions : [];
};
